/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { getPositionFromStop, getStopFromMouseLocation, isColorInvalid, isStopInvalid } from './utils';
import { getChromaColor } from '../utils';
import { keys, useMouseMove } from '../../../services';
import { EuiButtonIcon } from '../../button';
import { EuiColorPicker } from '../color_picker';
import { EuiFlexGroup, EuiFlexItem } from '../../flex';
import { EuiFieldNumber, EuiFormRow } from '../../form';
import { EuiI18n } from '../../i18n';
import { EuiPopover } from '../../popover';
import { EuiScreenReaderOnly } from '../../accessibility';
import { EuiSpacer } from '../../spacer';
import { EuiRangeThumb } from '../../form/range/range_thumb';
export var EuiColorStopThumb = ({
  className,
  stop,
  color,
  onChange,
  onFocus,
  onRemove,
  globalMin,
  globalMax,
  localMin,
  localMax,
  min,
  max,
  isRangeMin = false,
  isRangeMax = false,
  parentRef,
  colorPickerMode,
  colorPickerShowAlpha,
  colorPickerSwatches,
  disabled,
  readOnly,
  isPopoverOpen,
  openPopover,
  closePopover,
  'data-index': dataIndex,
  'aria-valuetext': ariaValueText,
  valueInputProps = {}
}) => {
  const background = useMemo(() => {
    const chromaColor = getChromaColor(color, colorPickerShowAlpha);
    return chromaColor ? chromaColor.css() : undefined;
  }, [color, colorPickerShowAlpha]);
  const [hasFocus, setHasFocus] = useState(isPopoverOpen);
  const [colorIsInvalid, setColorIsInvalid] = useState(isColorInvalid(color, colorPickerShowAlpha));
  const [stopIsInvalid, setStopIsInvalid] = useState(isStopInvalid(stop));
  const [numberInputRef, setNumberInputRef] = useState(null);
  const popoverRef = useRef(null);
  useEffect(() => {
    if (isPopoverOpen && popoverRef && popoverRef.current) {
      popoverRef.current.positionPopoverFixed();
    }
  }, [isPopoverOpen, stop]);

  const getStopFromMouseLocationFn = location => {
    // Guard against `null` ref in usage
    return getStopFromMouseLocation(location, parentRef, globalMin, globalMax);
  };

  const getPositionFromStopFn = stop => {
    // Guard against `null` ref in usage
    return getPositionFromStop(stop, parentRef, globalMin, globalMax);
  };

  const handleOnRemove = () => {
    if (onRemove) {
      closePopover();
      onRemove();
    }
  };

  const handleFocus = () => {
    setHasFocus(true);

    if (onFocus) {
      onFocus();
    }
  };

  const setHasFocusTrue = () => setHasFocus(true);

  const setHasFocusFalse = () => setHasFocus(false);

  const handleColorChange = value => {
    setColorIsInvalid(isColorInvalid(value, colorPickerShowAlpha));
    onChange({
      stop,
      color: value
    });
  };

  const handleStopChange = value => {
    const willBeInvalid = value > localMax || value < localMin;

    if (willBeInvalid) {
      if (value > localMax) {
        value = localMax;
      }

      if (value < localMin) {
        value = localMin;
      }
    }

    setStopIsInvalid(isStopInvalid(value));
    onChange({
      stop: value,
      color
    });
  };

  const handleStopInputChange = e => {
    let value = parseFloat(e.target.value);
    const willBeInvalid = value > globalMax || value < globalMin;

    if (willBeInvalid) {
      if (value > globalMax && max != null) {
        value = globalMax;
      }

      if (value < globalMin && min != null) {
        value = globalMin;
      }
    }

    setStopIsInvalid(isStopInvalid(value));
    onChange({
      stop: value,
      color
    });
  };

  const handlePointerChange = (location, isFirstInteraction) => {
    if (isFirstInteraction) return; // Prevents change on the initial MouseDown event

    if (parentRef == null) {
      return;
    }

    const newStop = getStopFromMouseLocationFn(location);
    handleStopChange(newStop);
  };

  const handleKeyDown = event => {
    switch (event.key) {
      case keys.ENTER:
        event.preventDefault();
        openPopover();
        break;

      case keys.ARROW_LEFT:
        event.preventDefault();
        if (readOnly) return;
        handleStopChange(stop - 1);
        break;

      case keys.ARROW_RIGHT:
        event.preventDefault();
        if (readOnly) return;
        handleStopChange(stop + 1);
        break;
    }
  };

  const [handleMouseDown, handleInteraction] = useMouseMove(handlePointerChange);

  const handleOnMouseDown = e => {
    if (!readOnly) {
      handleMouseDown(e);
    }

    openPopover();
  };

  const handleTouchInteraction = e => {
    if (!readOnly) {
      handleInteraction(e);
    }
  };

  const handleTouchStart = e => {
    handleTouchInteraction(e);

    if (!isPopoverOpen) {
      openPopover();
    }
  };

  const classes = classNames('euiColorStopPopover', {
    'euiColorStopPopover-hasFocus': hasFocus || isPopoverOpen
  }, className);
  return <EuiPopover ref={popoverRef} className={classes} anchorClassName="euiColorStopPopover__anchor" panelPaddingSize="s" isOpen={isPopoverOpen} closePopover={closePopover} initialFocus={numberInputRef || undefined} focusTrapProps={{
    clickOutsideDisables: false
  }} panelClassName={numberInputRef ? undefined : 'euiColorStopPopover-isLoadingPanel'} style={{
    left: `${getPositionFromStopFn(stop)}%`
  }} button={<EuiI18n tokens={['euiColorStopThumb.buttonAriaLabel', 'euiColorStopThumb.buttonTitle']} defaults={['Press the Enter key to modify this stop. Press Escape to focus the group', 'Click to edit, drag to reposition']}>
          {([buttonAriaLabel, buttonTitle]) => {
      const ariaLabel = buttonAriaLabel;
      const title = buttonTitle;
      return <EuiRangeThumb data-test-subj="euiColorStopThumb" data-index={dataIndex} min={localMin} max={localMax} value={stop} onFocus={handleFocus} onBlur={setHasFocusFalse} onMouseOver={setHasFocusTrue} onMouseOut={setHasFocusFalse} onKeyDown={handleKeyDown} onMouseDown={handleOnMouseDown} onTouchStart={handleTouchStart} onTouchMove={handleTouchInteraction} aria-valuetext={ariaValueText} aria-label={ariaLabel} title={title} className="euiColorStopThumb" tabIndex={-1} style={{
        background
      }} disabled={disabled} />;
    }}
        </EuiI18n>}>
      <div className="euiColorStop" data-test-subj="euiColorStopPopover">
        <EuiScreenReaderOnly>
          <p aria-live="polite">
            <EuiI18n token="euiColorStopThumb.screenReaderAnnouncement" default="A popup with a color stop edit form opened.
            Tab forward to cycle through form controls or press
            escape to close this popup." />
          </p>
        </EuiScreenReaderOnly>
        <EuiFlexGroup gutterSize="s" responsive={false}>
          <EuiFlexItem>
            <EuiI18n tokens={['euiColorStopThumb.stopLabel', 'euiColorStopThumb.stopErrorMessage']} defaults={['Stop value', 'Value is out of range']}>
              {([stopLabel, stopErrorMessage]) => <EuiFormRow label={stopLabel} display="rowCompressed" isInvalid={stopIsInvalid} error={stopIsInvalid ? stopErrorMessage : null}>
                  <EuiFieldNumber {...valueInputProps} inputRef={setNumberInputRef} compressed={true} readOnly={readOnly} min={isRangeMin || min == null ? undefined : localMin} max={isRangeMax || max == null ? undefined : localMax} value={isStopInvalid(stop) ? '' : stop} isInvalid={stopIsInvalid} onChange={handleStopInputChange} />
                </EuiFormRow>}
            </EuiI18n>
          </EuiFlexItem>
          {!readOnly && <EuiFlexItem grow={false}>
              <EuiFormRow display="rowCompressed" hasEmptyLabelSpace={true}>
                <EuiI18n token="euiColorStopThumb.removeLabel" default="Remove this stop">
                  {removeLabel => <EuiButtonIcon iconType="trash" color="danger" aria-label={removeLabel} title={removeLabel} disabled={!onRemove} onClick={handleOnRemove} />}
                </EuiI18n>
              </EuiFormRow>
            </EuiFlexItem>}
        </EuiFlexGroup>
        {!readOnly && <EuiSpacer size="s" />}
        <EuiColorPicker readOnly={readOnly} onChange={handleColorChange} color={color} mode={readOnly ? 'secondaryInput' : colorPickerMode} swatches={colorPickerSwatches} display="inline" showAlpha={colorPickerShowAlpha} isInvalid={colorIsInvalid} secondaryInputDisplay={colorPickerMode === 'swatch' ? 'none' : 'bottom'} />
      </div>
    </EuiPopover>;
};
EuiColorStopThumb.propTypes = {
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onRemove: PropTypes.func,
  globalMin: PropTypes.number.isRequired,
  globalMax: PropTypes.number.isRequired,
  localMin: PropTypes.number.isRequired,
  localMax: PropTypes.number.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  isRangeMin: PropTypes.bool,
  isRangeMax: PropTypes.bool,
  parentRef: PropTypes.oneOfType([PropTypes.any.isRequired, PropTypes.oneOf([null])]),
  colorPickerMode: PropTypes.oneOf(["default", "swatch", "picker", "secondaryInput"]).isRequired,
  colorPickerShowAlpha: PropTypes.bool,
  colorPickerSwatches: PropTypes.arrayOf(PropTypes.string.isRequired),
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  isPopoverOpen: PropTypes.bool.isRequired,
  openPopover: PropTypes.func.isRequired,
  closePopover: PropTypes.func.isRequired,
  "data-index": PropTypes.string,
  "aria-valuetext": PropTypes.string,
  valueInputProps: PropTypes.any,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  stop: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired
};

try {
  EuiColorStopThumb.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
    description: '',
    displayName: 'EuiColorStopThumb',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onChange: {
        defaultValue: null,
        description: '',
        name: 'onChange',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: true,
        type: {
          name: '(colorStop: ColorStop) => void'
        }
      },
      onFocus: {
        defaultValue: null,
        description: '',
        name: 'onFocus',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: false,
        type: {
          name: '() => void'
        }
      },
      onRemove: {
        defaultValue: null,
        description: '',
        name: 'onRemove',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: false,
        type: {
          name: '() => void'
        }
      },
      globalMin: {
        defaultValue: null,
        description: '',
        name: 'globalMin',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: true,
        type: {
          name: 'number'
        }
      },
      globalMax: {
        defaultValue: null,
        description: '',
        name: 'globalMax',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: true,
        type: {
          name: 'number'
        }
      },
      localMin: {
        defaultValue: null,
        description: '',
        name: 'localMin',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: true,
        type: {
          name: 'number'
        }
      },
      localMax: {
        defaultValue: null,
        description: '',
        name: 'localMax',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: true,
        type: {
          name: 'number'
        }
      },
      min: {
        defaultValue: null,
        description: '',
        name: 'min',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      max: {
        defaultValue: null,
        description: '',
        name: 'max',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      isRangeMin: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'isRangeMin',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isRangeMax: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'isRangeMax',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      parentRef: {
        defaultValue: null,
        description: '',
        name: 'parentRef',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: false,
        type: {
          name: 'HTMLDivElement'
        }
      },
      colorPickerMode: {
        defaultValue: null,
        description: '',
        name: 'colorPickerMode',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: true,
        type: {
          name: 'enum',
          raw: 'EuiColorPickerMode',
          value: [{
            value: '"default"'
          }, {
            value: '"swatch"'
          }, {
            value: '"picker"'
          }, {
            value: '"secondaryInput"'
          }]
        }
      },
      colorPickerShowAlpha: {
        defaultValue: null,
        description: '',
        name: 'colorPickerShowAlpha',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      colorPickerSwatches: {
        defaultValue: null,
        description: '',
        name: 'colorPickerSwatches',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: false,
        type: {
          name: 'string[]'
        }
      },
      disabled: {
        defaultValue: null,
        description: '',
        name: 'disabled',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      readOnly: {
        defaultValue: null,
        description: '',
        name: 'readOnly',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isPopoverOpen: {
        defaultValue: null,
        description: '',
        name: 'isPopoverOpen',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: true,
        type: {
          name: 'boolean'
        }
      },
      openPopover: {
        defaultValue: null,
        description: '',
        name: 'openPopover',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: true,
        type: {
          name: '() => void'
        }
      },
      closePopover: {
        defaultValue: null,
        description: '',
        name: 'closePopover',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: true,
        type: {
          name: '() => void'
        }
      },
      'data-index': {
        defaultValue: null,
        description: '',
        name: 'data-index',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-valuetext': {
        defaultValue: null,
        description: '',
        name: 'aria-valuetext',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      valueInputProps: {
        defaultValue: {
          value: '{}'
        },
        description: '',
        name: 'valueInputProps',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'EuiColorStopThumbProps'
        }],
        required: false,
        type: {
          name: 'Partial<EuiFieldNumberProps>'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      stop: {
        defaultValue: null,
        description: '',
        name: 'stop',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'ColorStop'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'ColorStop'
        }],
        required: true,
        type: {
          name: 'number'
        }
      },
      color: {
        defaultValue: null,
        description: '',
        name: 'color',
        parent: {
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'ColorStop'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/color_stops/color_stop_thumb.tsx',
          name: 'ColorStop'
        }],
        required: true,
        type: {
          name: 'string'
        }
      }
    },
    extendedInterfaces: ['EuiColorStopThumbProps', 'CommonProps', 'ColorStop']
  };
} catch (e) {}